// ============================================================
// matchClients.ts
// Per-row client matching for the CSV importer (Option A: name-match,
// user confirms). Given each row's parsed forename+surname and the
// firm's existing clients, produce a per-row RESOLUTION describing what
// the import will do with that row: LINK it to an existing client, or
// CREATE a new one.
//
// ★ AI-SWAPPABLE: matchRowsToClients has a stable signature + return
// shape. Today it's exact case-insensitive name matching; a future AI
// layer can replace the function body (or wrap it) to pre-fill smarter
// suggestions — the RowResolution carries an optional `confidence` slot
// reserved for exactly that. Everything downstream (the review UI, the
// import loop) consumes RowResolution and doesn't care how it was made.
// ============================================================
import type { Client } from '../types/client.types'

export type RowResolutionAction = 'link' | 'create'

export interface RowResolution {
  rowIndex: number
  // What the import will do: attach the case to an existing client
  // (`link`, needs clientId) or make a new client (`create`).
  action: RowResolutionAction
  clientId?: string // set when action === 'link'
  // The name parsed from the row (for display + re-matching).
  forename: string
  surname: string
  // The auto-suggested match, when exactly one existing client shares
  // the name (drives the default + the "link to X" label).
  matchedClientId?: string
  matchedClientName?: string
  // True when MORE THAN ONE existing client shares the name — we do NOT
  // auto-link an ambiguous name; the row defaults to `create` and the
  // user may manually pick which existing client to link.
  ambiguous?: boolean
  // Reserved for a future AI matcher (0..1). Unused by the name matcher.
  confidence?: number
}

// Normalise a name part for comparison: trim, lowercase, collapse
// internal whitespace.
const normName = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ')

// Build the per-row resolutions. Exact match on (forename, surname),
// case-insensitive + whitespace-normalised:
//   - exactly one existing client with that name → LINK (suggested).
//   - more than one                              → AMBIGUOUS → CREATE
//     (user can override to link a specific one).
//   - none / row has no name                     → CREATE.
const normNi = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, '').toUpperCase()
const ymd = (s: string | null | undefined): string => (s ?? '').slice(0, 10)

// ★ Identity-aware: a NAME match alone isn't enough when the row carries a
// DOB or NI number. Two different "John Smith"s used to auto-link, attaching
// the case to the wrong person (and dropping the row's DOB/NI). Now:
//   - an exact NI-number match links (even if the name is spelt differently);
//   - name-matched candidates whose DOB / NI CONFLICT with the row's are
//     excluded (a different person) before deciding link / ambiguous / create.
export const matchRowsToClients = (
  rows: { forename: string; surname: string; dateOfBirth?: string | null; niNumber?: string | null }[],
  clients: Client[],
): RowResolution[] => {
  const byNi = new Map<string, Client[]>()
  for (const c of clients) {
    const ni = normNi(c.niNumber)
    if (!ni) continue
    const arr = byNi.get(ni)
    if (arr) arr.push(c)
    else byNi.set(ni, [c])
  }
  // Index existing clients by "forename|surname".
  const byName = new Map<string, Client[]>()
  for (const c of clients) {
    const key = `${normName(c.forename)}|${normName(c.surname)}`
    const arr = byName.get(key)
    if (arr) arr.push(c)
    else byName.set(key, [c])
  }

  return rows.map((r, i) => {
    const hasName = !!(r.forename.trim() || r.surname.trim())
    const key = `${normName(r.forename)}|${normName(r.surname)}`
    const rowNi = normNi(r.niNumber)
    const rowDob = ymd(r.dateOfBirth)
    const niHits = rowNi ? byNi.get(rowNi) ?? [] : []
    const conflicts = (c: Client): boolean =>
      (!!rowDob && !!ymd(c.dateOfBirth) && ymd(c.dateOfBirth) !== rowDob)
      || (!!rowNi && !!normNi(c.niNumber) && normNi(c.niNumber) !== rowNi)
    const matches = niHits.length === 1
      ? niHits
      : (hasName ? byName.get(key) ?? [] : []).filter((c) => !conflicts(c))

    let resolution: RowResolution
    if (matches.length === 1) {
      const m = matches[0]
      resolution = {
        rowIndex: i,
        action: 'link',
        clientId: m.id,
        forename: r.forename,
        surname: r.surname,
        matchedClientId: m.id,
        matchedClientName: `${m.forename} ${m.surname}`.trim() || `Client #${m.clientNumber}`,
      }
    } else if (matches.length > 1) {
      // Ambiguous — never auto-link. Default to create; user resolves.
      resolution = { rowIndex: i, action: 'create', forename: r.forename, surname: r.surname, ambiguous: true }
    } else {
      resolution = { rowIndex: i, action: 'create', forename: r.forename, surname: r.surname }
    }
    return resolution
  })
}
